import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { concat, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  flatMap,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  ActionsEnum as XeroActionsEnum,
  AuthorizeAccount,
  authorizeAccountError,
  AuthorizeAccountError,
  authorizeAccountSuccess,
  AuthorizeAccountSuccess,
  DeleteXeroAccount,
  DeleteXeroAccountError,
  deleteXeroAccountError,
  DeleteXeroAccountSuccess,
  deleteXeroAccountSuccess,
  ExportDocumentToXero,
  exportDocumentToXeroError,
  ExportDocumentToXeroError,
  exportDocumentToXeroSuccess,
  ExportDocumentToXeroSuccess,
  fetchXeroAccount,
  FetchXeroAccount,
  FetchXeroAccountError,
  fetchXeroAccountError,
  FetchXeroAccountSuccess,
  fetchXeroAccountSuccess,
  UpdateXeroAccount,
  updateXeroAccount,
  UpdateXeroAccountNetwork,
  UpdateXeroAccountNetworkError,
  updateXeroAccountNetworkError,
  updateXeroAccountNetworkSuccess,
  UpdateXeroAccountNetworkSuccess,
} from './xero-data.actions';
import { ActionsEnum as AuthActionsEnum } from '../auth-data/auth-data.actions';
import { v4 } from 'uuid';
import { XeroIntegrationService } from './xero-integration.service';
import { XeroAccount } from './xero-account.model';
import { IResponseInfo } from '../http/http.data';
import { XeroDataStore } from './xero-data.store';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { XeroAuthUrl } from './sub-models/xero-auth-url.model';
import { NavigationService } from '../navigation/navigation.service';
import * as fromToast from '../toaster/toaster.actions';
import { TranslateService } from '@ngx-translate/core';
import { IToasterConfig, IToasterData } from '../toaster/toaster.data';
import { MessageToastComponent } from '../shared/components/message-toast/message-toast.component';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { DialogService } from '../dialog/dialog.service';

@Injectable()
export class XeroDataEffects {
  constructor(
    private _actions: Actions,
    private _xeroDataStore: XeroDataStore,
    private _featuresDataStore: FeaturesDataStore,
    private _navigationService: NavigationService,
    private _dialogService: DialogService,
    private _translateService: TranslateService,
    private _globalBannerService: GlobalBannerService,
    private _xeroIntegrationService: XeroIntegrationService
  ) {}

  @Effect()
  onAuth: Observable<FetchXeroAccount> = this._actions.pipe(
    ofType(AuthActionsEnum.AUTHENTICATED),
    flatMap(() => this._featuresDataStore.getFeatureToggles()),
    filter(
      (featuresToggles: Toggles) =>
        !featuresToggles.isForbidden(Toggles.KEYS.XERO)
    ),
    map(() => fetchXeroAccount(v4()))
  );

  @Effect()
  fetchXeroAccount: Observable<
    UpdateXeroAccount | FetchXeroAccountSuccess | FetchXeroAccountError
  > = this._actions.pipe(
    ofType(XeroActionsEnum.FETCH_XERO_ACCOUNT),
    switchMap(({ payload }: FetchXeroAccount) => {
      const { dispatchId } = payload;

      return this._xeroIntegrationService.getAccountStatus().pipe(
        take(1),
        flatMap((xeroAccount: XeroAccount) =>
          concat([
            updateXeroAccount(xeroAccount),
            fetchXeroAccountSuccess(dispatchId),
          ])
        ),
        catchError((error) => of(fetchXeroAccountError(dispatchId, error)))
      );
    })
  );

  @Effect()
  authorizeAccount: Observable<
    AuthorizeAccountSuccess | AuthorizeAccountError
  > = this._actions.pipe(
    ofType(XeroActionsEnum.AUTHORIZE_ACCOUNT),
    switchMap(({ payload }: AuthorizeAccount) => {
      const { dispatchId } = payload;

      return this._xeroIntegrationService.authorizeAccount().pipe(
        take(1),
        map((xeroAuthUrl: XeroAuthUrl) =>
          authorizeAccountSuccess(dispatchId, xeroAuthUrl.authorizationUrl)
        ),
        catchError((error) => of(authorizeAccountError(dispatchId, error)))
      );
    })
  );

  @Effect({ dispatch: false })
  authorizeAccountSuccess: Observable<any> = this._actions.pipe(
    ofType(XeroActionsEnum.AUTHORIZE_ACCOUNT_SUCCESS),
    tap(({ payload }: AuthorizeAccountSuccess) => {
      const { xeroAuthUrl } = payload;

      this._navigationService.goToExternalUrl(xeroAuthUrl);
    })
  );

  @Effect()
  exportDocumentToXero: Observable<
    ExportDocumentToXeroSuccess | ExportDocumentToXeroError
  > = this._actions.pipe(
    ofType(XeroActionsEnum.EXPORT_DOCUMENT_TO_XERO),
    switchMap(({ payload }: ExportDocumentToXero) => {
      const { dispatchId, documentId, documentNumber } = payload;

      this._dialogService.openLoadingDialog(
        this._translateService.instant('XERO.LABEL.EXPORTING')
      );

      return this._xeroIntegrationService.exportDocument(documentId).pipe(
        map(() =>
          exportDocumentToXeroSuccess(dispatchId, documentId, documentNumber)
        ),
        finalize(() => this._dialogService.closeLoadingDialog()),
        catchError((error) =>
          of(exportDocumentToXeroError(dispatchId, documentNumber, error))
        )
      );
    })
  );

  @Effect()
  exportDocumentToXeroSuccess: Observable<fromToast.ShowToast> = this._actions.pipe(
    ofType(XeroActionsEnum.EXPORT_DOCUMENT_TO_XERO_SUCCESS),
    map(({ payload }: ExportDocumentToXeroSuccess) => {
      const { documentNumber } = payload;
      const label = this._translateService.instant('XERO.EXPORT_SUCCESS_TEXT', {
        invoiceNumber: documentNumber,
      });
      const toastData: IToasterData = { label };
      const toastConfig: IToasterConfig = {
        type: 'export',
        component: MessageToastComponent,
        data: toastData,
      };

      return fromToast.showToast(toastConfig);
    })
  );

  @Effect({ dispatch: false })
  exportDocumentToXeroError = this._actions.pipe(
    ofType(XeroActionsEnum.EXPORT_DOCUMENT_TO_XERO_ERROR),
    tap(({ payload }: ExportDocumentToXeroError) => {
      const { documentNumber, error } = payload;
      const [_, errorBody, e] = error;
      const errorCodes = errorBody.metadata && errorBody.metadata.error_codes;

      const errorText = errorCodes
        ? this._translateService.instant('XERO.EXPORT_ERROR_TEXT_WITH_CODE', {
            invoiceNumber: documentNumber,
            errorCode: errorCodes,
          })
        : this._translateService.instant('XERO.EXPORT_ERROR_TEXT', {
            invoiceNumber: documentNumber,
          });

      this._globalBannerService.showSystemErrorBanner(e, errorText);
    })
  );

  @Effect()
  deleteXeroAccount: Observable<
    DeleteXeroAccountSuccess | DeleteXeroAccountError | UpdateXeroAccount
  > = this._actions.pipe(
    ofType(XeroActionsEnum.DELETE_XERO_ACCOUNT),
    switchMap(({ payload }: DeleteXeroAccount) => {
      const { dispatchId } = payload;

      return this._xeroIntegrationService.deleteXeroAccount().pipe(
        flatMap(() =>
          concat([
            updateXeroAccount(new XeroAccount()),
            deleteXeroAccountSuccess(dispatchId),
          ])
        ),
        catchError((error) => of(deleteXeroAccountError(dispatchId, error)))
      );
    })
  );

  @Effect()
  updateXeroAccountNetwork: Observable<
    | UpdateXeroAccount
    | UpdateXeroAccountNetworkSuccess
    | UpdateXeroAccountNetworkError
  > = this._actions.pipe(
    ofType(XeroActionsEnum.UPDATE_XERO_ACCOUNT_NETWORK),
    switchMap(({ payload }: UpdateXeroAccountNetwork) => {
      const {
        autoExport,
        defaultAccountCode,
        country,
        i2gCurrency,
        dispatchId,
      } = payload;

      return this._xeroIntegrationService
        .updateAccountStatus(
          autoExport,
          defaultAccountCode,
          country,
          i2gCurrency
        )
        .pipe(
          withLatestFrom(this._xeroDataStore.xeroAccount$),
          flatMap(([{ body }, xeroAccount]: [IResponseInfo, XeroAccount]) => {
            const updatedXeroAccount: XeroAccount = new XeroAccount(
              xeroAccount.merge(body)
            );

            return concat([
              updateXeroAccount(updatedXeroAccount),
              updateXeroAccountNetworkSuccess(dispatchId),
            ]);
          }),
          catchError((error) =>
            of(updateXeroAccountNetworkError(dispatchId, error))
          )
        );
    })
  );
}
